// src/lib/state/mlPredictions.state.svelte.ts
// NOTE: This file requires the .svelte.ts extension because it uses $state
//
// ============================================================================
// ML PREDICTIONS STATE - Batch Failure Predictions
// ============================================================================
// Manages state for batch ML predictions with progressive loading.
// Uses $state.raw for the results array (potentially large dataset).
// ============================================================================

import { createLogger } from '$lib/utils/logger';

const log = createLogger('state:ml-predictions');

// ============================================================================
// TYPES
// ============================================================================

export type BatchStatus = 'idle' | 'running' | 'complete' | 'cancelled' | 'error';
export type RiskLevel = 'Critical' | 'High' | 'Medium' | 'Low' | 'Minimal' | 'Unknown';

export interface MLPredictionResult {
	alert_id: string | null;
	hardware_id: number;
	site_id: number;
	asset_name: string | null;
	alert_type: string | null;
	has_alert: boolean;
	failure_probability: number;
	risk_level: RiskLevel;
	prediction: number;
	top_risk_factors: string[];
	predicted_failure_type: string | null;
	predicted_failure_type_label: string | null;
	failure_type_confidence: number | null;
	predicted_type_matches_alert: boolean | null;
	rank: number | null;
	status: string;
	generated_at: string | null;
	alert_start_time?: string | null;
}

export interface BatchState {
	batch_id: string | null;
	status: BatchStatus;
	total_alerts: number;
	total_inverters: number; // Inverter count from WebSocket batch_started (accurate total)
	completed_count: number;
	failed_count: number;
	progress_percent: number;
	error_message: string | null;
}

// ============================================================================
// RAW STATE FOR LARGE ARRAYS
// ============================================================================
// Results array uses $state.raw for performance (no deep proxy wrapping).
// Must be reassigned to trigger reactivity (not mutated in place).
// ============================================================================

let _mlPredictionResults = $state.raw<MLPredictionResult[]>([]);

// Batch processing metadata - regular reactive state
const _batchMetadata = $state<BatchState>({
	batch_id: null,
	status: 'idle',
	total_alerts: 0,
	total_inverters: 0,
	completed_count: 0,
	failed_count: 0,
	progress_percent: 0,
	error_message: null
});

// Loading states
const _loadingState = $state({
	isLoadingCached: false,
	isStartingBatch: false
});

// ============================================================================
// EXPORTED STATE OBJECT WITH GETTER/SETTER PATTERN
// ============================================================================

export const mlPredictionsState = {
	// Results array - raw state for performance
	get results() {
		return _mlPredictionResults;
	},
	set results(v: MLPredictionResult[]) {
		_mlPredictionResults = v;
	},

	// Batch state
	get batchId() {
		return _batchMetadata.batch_id;
	},
	set batchId(v: string | null) {
		_batchMetadata.batch_id = v;
	},

	get status() {
		return _batchMetadata.status;
	},
	set status(v: BatchStatus) {
		_batchMetadata.status = v;
	},

	get totalAlerts() {
		return _batchMetadata.total_alerts;
	},
	set totalAlerts(v: number) {
		_batchMetadata.total_alerts = v;
	},

	get totalInverters() {
		return _batchMetadata.total_inverters;
	},
	set totalInverters(v: number) {
		_batchMetadata.total_inverters = v;
	},

	get completedCount() {
		return _batchMetadata.completed_count;
	},
	set completedCount(v: number) {
		_batchMetadata.completed_count = v;
	},

	get failedCount() {
		return _batchMetadata.failed_count;
	},
	set failedCount(v: number) {
		_batchMetadata.failed_count = v;
	},

	get progressPercent() {
		return _batchMetadata.progress_percent;
	},
	set progressPercent(v: number) {
		_batchMetadata.progress_percent = v;
	},

	get errorMessage() {
		return _batchMetadata.error_message;
	},
	set errorMessage(v: string | null) {
		_batchMetadata.error_message = v;
	},

	// Loading states
	get isLoadingCached() {
		return _loadingState.isLoadingCached;
	},
	set isLoadingCached(v: boolean) {
		_loadingState.isLoadingCached = v;
	},

	get isStartingBatch() {
		return _loadingState.isStartingBatch;
	},
	set isStartingBatch(v: boolean) {
		_loadingState.isStartingBatch = v;
	}
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a prediction result is valid (complete with valid probability)
 */
export function isValidPrediction(prediction: MLPredictionResult): boolean {
	// Must have status 'complete'
	if (prediction.status !== 'complete') {
		return false;
	}
	// Must have a valid failure_probability (not NaN, null, undefined)
	if (
		prediction.failure_probability === null ||
		prediction.failure_probability === undefined ||
		Number.isNaN(prediction.failure_probability)
	) {
		return false;
	}
	// Must have a hardware_id
	if (!prediction.hardware_id) {
		return false;
	}
	return true;
}

/**
 * Check if batch is currently running
 */
export function isBatchRunning(): boolean {
	return _batchMetadata.status === 'running';
}

/**
 * Check if we have any results
 */
export function hasResults(): boolean {
	return _mlPredictionResults.length > 0;
}

/**
 * Get results count
 */
export function getResultsCount(): number {
	return _mlPredictionResults.length;
}

/**
 * Get risk level color class for styling
 */
export function getRiskLevelColor(riskLevel: RiskLevel): string {
	const colors: Record<RiskLevel, string> = {
		Critical: 'text-red-400 dark:text-red-300',
		High: 'text-orange-600 dark:text-orange-400',
		Medium: 'text-yellow-600 dark:text-yellow-400',
		Low: 'text-blue-600 dark:text-blue-400',
		Minimal: 'text-green-600 dark:text-green-400',
		Unknown: 'text-gray-600 dark:text-gray-400'
	};
	return colors[riskLevel] || colors.Unknown;
}

/**
 * Get risk level badge class for styling
 */
export function getRiskLevelBadgeClass(riskLevel: RiskLevel): string {
	const badges: Record<RiskLevel, string> = {
		Critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
		High: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
		Medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
		Low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
		Minimal: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
		Unknown: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
	};
	return badges[riskLevel] || badges.Unknown;
}

/** Derived lookup map: hardware_id → MLPredictionResult (O(1) access) */
const _predictionsByHardwareId = $derived.by(() => {
	const map = new Map<number, MLPredictionResult>();
	for (const pred of _mlPredictionResults) {
		if (pred.status === 'complete' && pred.hardware_id) {
			map.set(pred.hardware_id, pred);
		}
	}
	return map;
});

/** Get prediction for an alert by its component_id (hardware_id string) */
export function getPredictionForAlert(
	componentId: string | undefined
): MLPredictionResult | undefined {
	if (!componentId) return undefined;
	const hwId = parseInt(componentId, 10);
	if (isNaN(hwId) || hwId === 0) return undefined;
	return _predictionsByHardwareId.get(hwId);
}

/**
 * Reset batch state to idle
 */
export function resetBatchState(): void {
	_batchMetadata.batch_id = null;
	_batchMetadata.status = 'idle';
	_batchMetadata.total_alerts = 0;
	_batchMetadata.total_inverters = 0;
	_batchMetadata.completed_count = 0;
	_batchMetadata.failed_count = 0;
	_batchMetadata.progress_percent = 0;
	_batchMetadata.error_message = null;
}

/**
 * Clear all results
 */
export function clearResults(): void {
	_mlPredictionResults = [];
	resetBatchState();
}

// ============================================================================
// ACTIONS - State Mutations
// ============================================================================

/**
 * Set cached predictions from API response
 */
export function setCachedPredictions(predictions: MLPredictionResult[]): void {
	// Filter out invalid predictions (errors, NaN probabilities, unknown assets)
	const validPredictions = predictions.filter(isValidPrediction);

	// Set sequential ranks (results are already sorted by probability from the API)
	const rankedPredictions = validPredictions.map((pred, index) => ({
		...pred,
		rank: index + 1
	}));

	_mlPredictionResults = rankedPredictions;
	log.debug(`Set ${rankedPredictions.length} cached predictions (filtered ${predictions.length - rankedPredictions.length} invalid)`);
}

/**
 * Handle batch started - initialize batch state
 */
export function handleBatchStarted(
	batchId: string,
	totalAlerts: number,
	totalInverters: number,
	cachedCount: number
): void {
	_batchMetadata.batch_id = batchId;
	_batchMetadata.status = 'running';
	_batchMetadata.total_alerts = totalAlerts;
	_batchMetadata.total_inverters = totalInverters;
	_batchMetadata.completed_count = 0;
	_batchMetadata.failed_count = 0;
	_batchMetadata.progress_percent = 0;
	_batchMetadata.error_message = null;
	log.info(`Batch started: ${batchId}, ${totalInverters} inverters across ${totalAlerts} alerts, ${cachedCount} cached`);
}

/**
 * Handle prediction complete - insert result in sorted position, dedup by hardware_id
 */
export function handlePredictionComplete(
	prediction: MLPredictionResult,
	completedCount: number,
	failedCount: number,
	totalCount: number
): void {
	// Update counts regardless of whether this prediction is valid
	_batchMetadata.completed_count = completedCount;
	_batchMetadata.failed_count = failedCount;
	// Use totalInverters from batch_started (accurate) or fall back to totalCount from message
	const total = _batchMetadata.total_inverters || totalCount || 1;
	_batchMetadata.progress_percent = Math.min(Math.round(((completedCount + failedCount) / total) * 100), 99);

	// Skip invalid predictions (errors, NaN probabilities)
	if (!isValidPrediction(prediction)) {
		log.debug(`Skipping invalid prediction: hw=${prediction.hardware_id} (status=${prediction.status}, prob=${prediction.failure_probability})`);
		return;
	}

	// Remove any existing result for this hardware_id (dedup)
	const currentResults = _mlPredictionResults.filter(
		(r) => r.hardware_id !== prediction.hardware_id
	);

	// Insert result in sorted position (highest probability first)
	// Use binary search for O(log n) insertion
	const newProb = prediction.failure_probability;

	let insertIndex = 0;
	let low = 0;
	let high = currentResults.length;

	while (low < high) {
		const mid = Math.floor((low + high) / 2);
		if (currentResults[mid].failure_probability > newProb) {
			low = mid + 1;
		} else {
			high = mid;
		}
	}
	insertIndex = low;

	// Insert at the correct position
	currentResults.splice(insertIndex, 0, prediction);

	// Update ranks for all items
	for (let i = 0; i < currentResults.length; i++) {
		currentResults[i].rank = i + 1;
	}

	// Reassign to trigger reactivity
	_mlPredictionResults = currentResults;

	log.debug(
		`Prediction complete: hw=${prediction.hardware_id} @ rank ${insertIndex + 1}, ` +
			`prob=${(prediction.failure_probability * 100).toFixed(1)}%`
	);
}

/**
 * Handle batch complete
 */
export function handleBatchComplete(completedCount: number, failedCount: number): void {
	_batchMetadata.status = 'complete';
	_batchMetadata.completed_count = completedCount;
	_batchMetadata.failed_count = failedCount;
	_batchMetadata.progress_percent = 100;
	log.info(`Batch complete: ${completedCount} succeeded, ${failedCount} failed`);
}

/**
 * Handle batch cancelled
 */
export function handleBatchCancelled(completedCount: number, failedCount: number): void {
	_batchMetadata.status = 'cancelled';
	_batchMetadata.completed_count = completedCount;
	_batchMetadata.failed_count = failedCount;
	log.info(`Batch cancelled: ${completedCount} completed before cancellation`);
}

/**
 * Handle batch error
 */
export function handleBatchError(errorMessage: string): void {
	_batchMetadata.status = 'error';
	_batchMetadata.error_message = errorMessage;
	log.error(`Batch error: ${errorMessage}`);
}

/**
 * Set loading state for cached predictions
 */
export function setLoadingCached(isLoading: boolean): void {
	_loadingState.isLoadingCached = isLoading;
}

/**
 * Set loading state for starting batch
 */
export function setStartingBatch(isStarting: boolean): void {
	_loadingState.isStartingBatch = isStarting;
}

/**
 * Process WebSocket TASK_PROGRESS message for batch_failure_prediction
 */
export function handleTaskProgress(message: {
	task_id: string;
	task_type: string;
	stage: string;
	progress: number;
	metadata?: {
		alert_id?: string;
		hardware_id?: number;
		prediction?: MLPredictionResult;
		completed_count?: number;
		failed_count?: number;
		total_count?: number;
		total_alerts?: number;
		cached_count?: number;
		sites_count?: number;
		sites_processed?: number;
		error?: string;
	};
}): void {
	// Only handle batch_failure_prediction messages
	if (message.task_type !== 'batch_failure_prediction') {
		return;
	}

	const { stage, metadata } = message;

	switch (stage) {
		case 'batch_started':
			handleBatchStarted(
				message.task_id,
				metadata?.total_alerts || 0,
				metadata?.total_count || 0, // total_count = total inverters from discovery phase
				metadata?.cached_count || 0
			);
			break;

		case 'site_cached':
			// Site was fully cached — update progress counters
			if (metadata) {
				_batchMetadata.completed_count = metadata.completed_count || _batchMetadata.completed_count;
				_batchMetadata.failed_count = metadata.failed_count || _batchMetadata.failed_count;
				const total = _batchMetadata.total_inverters || metadata.total_count || 1;
				_batchMetadata.progress_percent = Math.min(
					Math.round(((_batchMetadata.completed_count + _batchMetadata.failed_count) / total) * 100),
					99
				);
			}
			break;

		case 'prediction_complete':
			if (metadata?.prediction) {
				handlePredictionComplete(
					metadata.prediction,
					metadata.completed_count || 0,
					metadata.failed_count || 0,
					metadata.total_count || 1
				);
			}
			break;

		case 'batch_complete':
			handleBatchComplete(metadata?.completed_count || 0, metadata?.failed_count || 0);
			break;

		case 'batch_cancelled':
			handleBatchCancelled(metadata?.completed_count || 0, metadata?.failed_count || 0);
			break;

		case 'batch_error':
			handleBatchError(metadata?.error || 'Unknown error');
			break;

		default:
			log.debug(`Unknown batch stage: ${stage}`);
	}
}
